/**
 * Display formatters for the session tracker. Pure, presentation-only —
 * the load-bearing numbers come pre-computed from the backend
 * (facts.session_stats); these just render them.
 */

/** Signed money, no decimals: "+$1,240", "−$315", "$0". */
export function money(amount: number): string {
  const rounded = Math.round(amount);
  if (rounded === 0) return "$0";
  const sign = rounded > 0 ? "+" : "−";
  return `${sign}$${Math.abs(rounded).toLocaleString()}`;
}

/** Unsigned money for buy-ins etc.: "$200". */
export function moneyAbs(amount: number): string {
  return `$${Math.abs(Math.round(amount)).toLocaleString()}`;
}

/** Hours from minutes: "3h", "3h 30m". */
export function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Hours as a decimal label: "240 hours", "12.5 hours". */
export function hoursLabel(hours: number): string {
  const rounded = Math.round(hours * 10) / 10;
  return rounded === Math.round(rounded) ? `${Math.round(rounded)}h` : `${rounded}h`;
}

/** bb/hr with sign: "+5.2 bb/hr". */
export function bbPerHour(value: number): string {
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)} bb/hr`;
}

/** Tailwind text color for a net value (accent up, danger down, muted flat). */
export function netToneClass(net: number): string {
  if (net > 0) return "text-accent";
  if (net < 0) return "text-danger";
  return "text-text-muted";
}

const CONFIDENCE_LABEL: Record<string, string> = {
  noise: "Tiny sample",
  directional: "Directional",
  meaningful: "Meaningful sample",
};

export function confidenceLabel(confidence: string): string {
  return CONFIDENCE_LABEL[confidence] ?? confidence;
}

/** Distinct, non-empty values in first-seen order (sessions arrive newest
 *  first, so the most recently used values come first). Powers the
 *  quick-pick stake/location chips on the log form. */
export function recentDistinct(
  values: (string | null | undefined)[],
  limit = 8,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const v = (raw ?? "").trim();
    if (v && !seen.has(v)) {
      seen.add(v);
      out.push(v);
      if (out.length >= limit) break;
    }
  }
  return out;
}
